(function () {
  'use strict';

  var CART_EVENTS = [
    'on:cart:add',
    'on:line-item:change',
    'cart:updated',
    'cart:change',
    'shopify:cart:updated'
  ];

  function toNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }

  function toQuantity(value) {
    return Math.max(0, Math.floor(toNumber(value)));
  }

  function withJsonSuffix(url) {
    var raw = String(url || '');
    if (!raw) return '';
    if (/\.js(?:\?|$)/.test(raw)) return raw;
    var parts = raw.split('?');
    return parts[0].replace(/\/$/, '') + '.js' + (parts[1] ? '?' + parts[1] : '');
  }

  function tokens(value) {
    return String(value || '').split(/\s+/).filter(Boolean);
  }

  function unique(values) {
    return Array.from(new Set(values.filter(Boolean)));
  }

  function normalizeSearch(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function replaceTokens(template, values) {
    return String(template || '').replace(/\[([a-z_]+)\]/gi, function (match, key) {
      return values[key] !== undefined ? values[key] : match;
    });
  }

  function replaceTokensWithFallback(template, fallback, values) {
    var message = replaceTokens(template, values);
    if (/\[[^\]]+\]/.test(message) && fallback && fallback !== template) {
      message = replaceTokens(fallback, values);
    }
    return message;
  }

  function formatMoneyNoCode(amountMajor) {
    var amountNum = Number(amountMajor || 0);
    var cents = Math.round(amountNum * 100);
    var currency = window.Shopify?.currency?.active || 'USD';
    var value = cents / 100;

    try {
      var locale = currency === 'CAD' ? 'en-CA' : (document.documentElement?.lang || undefined);
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol'
      }).format(value);
    } catch (e) {
      /* noop */
    }

    if (window.Shopify?.formatMoney) {
      var formatted = window.Shopify.formatMoney(cents);
      var normalized = formatted
        .replace(/\u00a0/g, ' ')
        .replace(new RegExp('^' + currency + '\\s*\\$', 'i'), '$')
        .replace(/^[A-Z]{2}\s*\$/i, '$')
        .replace(/\s+[A-Z]{3}$/i, '')
        .trim();
      if (/[^\d\s.,-]/.test(normalized)) return normalized;
    }

    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: currency
    });
  }

  function parseCardArgs(card) {
    var script = card.querySelector('script.regios-dopp-card-args[type="application/json"]');
    if (!script?.textContent?.trim()) return null;

    var base;
    try {
      base = JSON.parse(script.textContent.trim());
    } catch (e) {
      return null;
    }

    var variantId = parseInt(card.dataset.variantId || '', 10);
    var regularPriceInCents = parseInt(card.dataset.regularPriceCents || '', 10);
    if (
      !Number.isInteger(variantId) ||
      variantId <= 0 ||
      !Number.isInteger(regularPriceInCents) ||
      regularPriceInCents < 0
    ) {
      return null;
    }

    return Object.assign({}, base, {
      variantId: variantId,
      regularPriceInCents: regularPriceInCents,
      compareAtPriceInCents: null,
      quantity: 1
    });
  }

  function renderBadge(card, percent) {
    var badge = card.querySelector('[data-regios-discount-badge]');
    if (!badge) return;

    var safePercent = Math.max(0, Math.round(toNumber(percent)));
    if (!safePercent) {
      badge.hidden = true;
      return;
    }

    var template = badge.dataset.badgeTemplate || '[percent]% OFF';
    var text = template.replace(/\[percent\]/gi, String(safePercent));
    if (/\[[^\]]+\]/.test(text) && badge.dataset.badgeFallback) {
      text = badge.dataset.badgeFallback.replace(/\[percent\]/gi, String(safePercent));
    }
    badge.textContent = text;
    badge.hidden = false;
  }

  function renderPrice(card, result) {
    var sale = Number(result?.salePrice?.amount);
    var regular = Number(result?.regularPrice?.amount);
    if (!Number.isFinite(sale) || !Number.isFinite(regular) || sale < 0 || regular <= 0) return;

    var priceRoot = card.querySelector('[data-regios-price]');
    var currentEl = priceRoot?.querySelector('.price__current');
    var wasEl = priceRoot?.querySelector('.price__was');
    if (!priceRoot || !currentEl || !wasEl) return;

    var discounted = sale < regular;
    currentEl.innerHTML = formatMoneyNoCode(discounted ? sale : regular);
    wasEl.innerHTML = discounted ? formatMoneyNoCode(regular) : '';
    wasEl.hidden = !discounted;
    wasEl.classList.toggle('visually-hidden', !discounted);
    wasEl.setAttribute('aria-hidden', String(!discounted));
    priceRoot.classList.toggle('price--on-sale', discounted);
    priceRoot.hidden = false;

    renderBadge(card, discounted ? Math.round((1 - sale / regular) * 100) : 0);
  }

  class LaborDayVariantDeals extends HTMLElement {
    constructor() {
      super();
      this.selected = new Map();
      this.filters = {
        material: new Set(),
        color: new Set(),
        keyword: new Set()
      };
      this.searchTerms = [];
      this.cartCount = 0;
      this.loading = false;
      this._cartSyncTimer = null;
      this._cartDrawerTimer = null;
    }

    connectedCallback() {
      this.eligibleVariantIds = new Set(this.readEligibleVariantIds());
      this.cartCount = toQuantity(this.querySelector('[data-labor-day-progress]')?.dataset.cartCount);
      this.tiers = [1, 2, 3, 4].map((index) => {
        return {
          enabled: this.getAttribute('data-tier-' + index + '-enabled') === 'true',
          count: toQuantity(this.getAttribute('data-tier-' + index + '-count')),
          percent: toNumber(this.getAttribute('data-tier-' + index + '-percent'))
        };
      }).filter((tier) => tier.enabled && tier.count > 0 && tier.percent > 0)
        .sort((a, b) => a.count - b.count);
      this.maxTierCount = this.tiers.length ? this.tiers[this.tiers.length - 1].count : 0;

      this.cards = Array.from(this.querySelectorAll('[data-labor-day-variant-card]'));
      this.filterButtons = Array.from(this.querySelectorAll('[data-labor-day-filter-button]'));
      this.searchInputEl = this.querySelector('[data-labor-day-search]');
      this.clearFiltersBtn = this.querySelector('[data-labor-day-clear-filters]');
      this.visibleCountEl = this.querySelector('[data-labor-day-visible-count]');
      this.noResultsEl = this.querySelector('[data-labor-day-no-results]');
      this.progressEl = this.querySelector('[data-labor-day-progress]');
      this.progressMessageEl = this.querySelector('[data-labor-day-progress-message]');
      this.progressCartEl = this.querySelector('[data-labor-day-progress-cart]');
      this.progressPendingEl = this.querySelector('[data-labor-day-progress-pending]');
      this.totalCountEl = this.querySelector('[data-labor-day-total-count]');
      this.summaryCountEl = this.querySelector('[data-labor-day-summary-count]');
      this.addButtonEl = this.querySelector('[data-labor-day-add-to-cart]');
      this.addButtonTextEl = this.querySelector('[data-labor-day-add-text]');
      this.errorEl = this.querySelector('[data-labor-day-error]');
      this.milestones = Array.from(this.querySelectorAll('[data-labor-day-milestone]'));
      this.labels = Array.from(this.querySelectorAll('[data-labor-day-label]'));

      this.cards.forEach((card) => {
        card.laborDaySearchText = normalizeSearch(card.dataset.search);
        card.laborDaySearchCompact = card.laborDaySearchText.replace(/\s+/g, '');
      });

      this._onClick = this.handleClick.bind(this);
      this._onInput = this.handleInput.bind(this);
      this._onFocusIn = this.handleFocusIn.bind(this);
      this._onSearchInput = this.handleSearchInput.bind(this);
      this._onCartEvent = this.scheduleCartSync.bind(this);
      this._onRegiosInitialized = this.syncRegiosPrices.bind(this);

      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onInput);
      this.addEventListener('input', this._onInput);
      this.addEventListener('focusin', this._onFocusIn);
      if (this.searchInputEl) this.searchInputEl.addEventListener('input', this._onSearchInput);
      CART_EVENTS.forEach((name) => document.addEventListener(name, this._onCartEvent));
      window.addEventListener('regios-dopp:api-initialized', this._onRegiosInitialized, { once: true });

      this.applyFilters();
      this.updateState();
      this.syncCartCount();
      this.syncRegiosPrices();
      requestAnimationFrame(() => this.syncRegiosPrices());
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onInput);
      this.removeEventListener('input', this._onInput);
      this.removeEventListener('focusin', this._onFocusIn);
      if (this.searchInputEl) this.searchInputEl.removeEventListener('input', this._onSearchInput);
      CART_EVENTS.forEach((name) => document.removeEventListener(name, this._onCartEvent));
      window.removeEventListener('regios-dopp:api-initialized', this._onRegiosInitialized);
      if (this._cartSyncTimer) clearTimeout(this._cartSyncTimer);
      if (this._cartDrawerTimer) clearTimeout(this._cartDrawerTimer);
    }

    readEligibleVariantIds() {
      var script = this.querySelector('[data-labor-day-eligible-variants]');
      if (!script?.textContent?.trim()) return [];
      try {
        var parsed = JSON.parse(script.textContent.trim());
        return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
      } catch (e) {
        return [];
      }
    }

    handleClick(event) {
      var filterButton = event.target.closest('[data-labor-day-filter-button]');
      if (filterButton && this.contains(filterButton)) {
        event.preventDefault();
        this.toggleFilter(filterButton);
        return;
      }

      if (event.target.closest('[data-labor-day-clear-filters]')) {
        event.preventDefault();
        this.clearFilters();
        return;
      }

      if (event.target.closest('[data-labor-day-add-to-cart]')) {
        event.preventDefault();
        this.addToCart();
        return;
      }

      var plus = event.target.closest('[data-labor-day-qty-plus]');
      var minus = event.target.closest('[data-labor-day-qty-minus]');
      if (!plus && !minus) return;

      var card = event.target.closest('[data-labor-day-variant-card]');
      if (!card) return;
      event.preventDefault();

      var input = card.querySelector('[data-labor-day-qty-input]');
      var current = input ? toQuantity(input.value) : this.getVariantQuantity(card.dataset.variantId);
      this.setVariantQuantity(card.dataset.variantId, plus ? current + 1 : current - 1);
    }

    handleInput(event) {
      if (!event.target.matches('[data-labor-day-qty-input]')) return;
      var card = event.target.closest('[data-labor-day-variant-card]');
      if (!card) return;
      this.setVariantQuantity(card.dataset.variantId, event.target.value);
    }

    handleFocusIn(event) {
      if (!event.target.matches('[data-labor-day-qty-input]')) return;
      requestAnimationFrame(() => {
        if (document.activeElement === event.target) event.target.select();
      });
    }

    handleSearchInput() {
      this.searchTerms = tokens(normalizeSearch(this.searchInputEl ? this.searchInputEl.value : ''));
      this.applyFilters();
    }

    toggleFilter(button) {
      var type = button.dataset.filterType;
      var value = button.dataset.filterValue;
      if (!this.filters[type] || !value) return;

      if (type !== 'color') {
        var wasActive = this.filters[type].has(value);
        this.filters[type].clear();
        if (!wasActive) this.filters[type].add(value);

        this.filterButtons.forEach((filterButton) => {
          if (filterButton.dataset.filterType !== type) return;
          var isActive = !wasActive && filterButton === button;
          filterButton.classList.toggle('is-active', isActive);
          filterButton.setAttribute('aria-pressed', String(isActive));
        });
      } else if (this.filters[type].has(value)) {
        this.filters[type].delete(value);
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      } else {
        this.filters[type].add(value);
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');
      }

      this.applyFilters();
    }

    clearFilters() {
      Object.keys(this.filters).forEach((key) => this.filters[key].clear());
      this.searchTerms = [];
      if (this.searchInputEl) this.searchInputEl.value = '';
      this.filterButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      this.applyFilters();
    }

    applyFilters() {
      var visibleCount = 0;

      this.cards.forEach((card) => {
        var visible = this.cardMatchesFilters(card);
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      if (this.visibleCountEl) this.visibleCountEl.textContent = String(visibleCount);
      if (this.noResultsEl) this.noResultsEl.hidden = visibleCount > 0;
      if (this.clearFiltersBtn) {
        var hasActiveFilters = Object.keys(this.filters).some((key) => this.filters[key].size);
        this.clearFiltersBtn.hidden = !hasActiveFilters && this.searchTerms.length === 0;
      }
    }

    cardMatchesFilters(card) {
      return this.matchesSearch(card) &&
        this.matchesTokenSet(this.filters.material, card.dataset.materials) &&
        this.matchesTokenSet(this.filters.color, card.dataset.color) &&
        this.matchesKeywordFilters(card);
    }

    matchesSearch(card) {
      if (!this.searchTerms.length) return true;
      var searchText = card.laborDaySearchText || '';
      var compactText = card.laborDaySearchCompact || '';
      return this.searchTerms.every((term) => searchText.includes(term) || compactText.includes(term));
    }

    matchesKeywordFilters(card) {
      if (!this.filters.keyword.size) return true;
      return Array.from(this.filters.keyword).every((keyword) => {
        var keywordTerms = tokens(normalizeSearch(keyword));
        return keywordTerms.length > 0 && keywordTerms.every((term) => {
          return card.laborDaySearchText.includes(term) || card.laborDaySearchCompact.includes(term);
        });
      });
    }

    matchesTokenSet(activeSet, cardValue) {
      if (!activeSet || activeSet.size === 0) return true;
      var cardTokens = tokens(cardValue);
      return Array.from(activeSet).every((token) => cardTokens.includes(token));
    }

    getVariantQuantity(variantId) {
      return this.selected.get(String(variantId)) || 0;
    }

    setVariantQuantity(variantId, value) {
      var id = String(variantId);
      var quantity = toQuantity(value);

      if (quantity > 0) {
        this.selected.set(id, quantity);
      } else {
        this.selected.delete(id);
      }

      this.syncVariantInput(id, quantity);
      this.updateState();
    }

    syncVariantInput(variantId, quantity) {
      var card = this.querySelector('[data-labor-day-variant-card][data-variant-id="' + variantId + '"]');
      if (!card) return;
      card.classList.toggle('is-selected', quantity > 0);
      var input = card.querySelector('[data-labor-day-qty-input]');
      if (input && input.value !== String(quantity)) input.value = String(quantity);
    }

    pendingCount() {
      var total = 0;
      this.selected.forEach((quantity) => {
        total += quantity;
      });
      return total;
    }

    evaluateMessage(cartCount, pendingCount) {
      if (!this.tiers.length) return '';

      var total = cartCount + pendingCount;
      var current = null;
      var next = null;

      for (var i = 0; i < this.tiers.length; i++) {
        if (total >= this.tiers[i].count) {
          current = this.tiers[i];
        } else {
          next = this.tiers[i];
          break;
        }
      }

      if (!current) {
        var first = this.tiers[0];
        var needed = Math.max(0, first.count - total);
        return (this.dataset.unlockTemplate || '')
          .replace(/\[count\]/g, String(needed))
          .replace(/\[percent\]/g, String(first.percent));
      }

      if (pendingCount > 0 && (cartCount < current.count || !next)) {
        return replaceTokensWithFallback(
          this.dataset.readyLabel || '',
          this.dataset.readyFallback || '',
          { percent: current.percent, current_percent: current.percent }
        );
      }

      if (next) {
        var remaining = Math.max(0, next.count - total);
        return replaceTokensWithFallback(
          this.dataset.progressLabel || '',
          this.dataset.progressFallback || '',
          {
            current_percent: current.percent,
            next_percent: next.percent,
            remaining: remaining,
            percent: current.percent
          }
        );
      }

      return replaceTokensWithFallback(
        this.dataset.unlockedLabel || '',
        this.dataset.unlockedFallback || '',
        { percent: current.percent, current_percent: current.percent }
      );
    }

    updateState() {
      var pending = this.pendingCount();
      var total = this.cartCount + pending;
      var max = this.maxTierCount || 1;
      var cartProgress = Math.min(this.cartCount, max);
      var pendingStart = Math.min(this.cartCount, max);
      var pendingEnd = Math.min(total, max);
      var pendingProgress = Math.max(0, pendingEnd - pendingStart);
      var message = this.evaluateMessage(this.cartCount, pending);

      if (this.progressMessageEl) this.progressMessageEl.textContent = message;
      if (this.progressCartEl) this.progressCartEl.style.width = (cartProgress / max * 100).toFixed(2) + '%';
      if (this.progressPendingEl) {
        this.progressPendingEl.style.left = (pendingStart / max * 100).toFixed(2) + '%';
        this.progressPendingEl.style.width = (pendingProgress / max * 100).toFixed(2) + '%';
      }
      if (this.totalCountEl) {
        var countTemplate = this.totalCountEl.dataset.itemsCountTemplate || '[count]';
        this.totalCountEl.textContent = countTemplate.replace(/\[count\]/g, String(total));
      }
      if (this.summaryCountEl) {
        this.summaryCountEl.textContent = (this.dataset.selectedTemplate || '[count]')
          .replace(/\[count\]/g, String(pending));
      }

      this.milestones.forEach((milestone) => {
        var count = toQuantity(milestone.dataset.count);
        var cartEarned = this.cartCount >= count;
        var pendingEarned = !cartEarned && total >= count;
        milestone.classList.toggle('is-earned', cartEarned || pendingEarned);
        milestone.classList.toggle('is-pending-earned', pendingEarned);
      });

      this.labels.forEach((label) => {
        var count = toQuantity(label.dataset.count);
        var cartEarned = this.cartCount >= count;
        var pendingEarned = !cartEarned && total >= count;
        label.classList.toggle('is-earned', cartEarned || pendingEarned);
        label.classList.toggle('is-pending-earned', pendingEarned);
      });

      if (this.progressEl) {
        this.progressEl.setAttribute('aria-valuenow', String(Math.min(total, max)));
        this.progressEl.setAttribute('aria-valuemax', String(max));
        this.progressEl.setAttribute('aria-valuetext', message);
      }

      if (this.addButtonEl) this.addButtonEl.disabled = pending === 0 || this.loading;
      if (this.addButtonTextEl) {
        this.addButtonTextEl.textContent = pending > 0
          ? (this.dataset.addItemsTemplate || '').replace(/\[count\]/g, String(pending))
          : (this.dataset.addLabel || '');
      }
    }

    scheduleCartSync() {
      if (this._cartSyncTimer) clearTimeout(this._cartSyncTimer);
      this._cartSyncTimer = setTimeout(() => this.syncCartCount(), 220);
    }

    async syncCartCount() {
      try {
        var cart = await this.fetchCart();
        if (cart) this.applyCart(cart);
      } catch (_) {
        /* Cart progress remains on the last known state. */
      }
    }

    async fetchCart() {
      var cartUrl = withJsonSuffix(this.dataset.cartUrl || (window.theme && theme.routes && theme.routes.cart) || '/cart');
      if (!cartUrl) return null;
      var response = await fetch(cartUrl, {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return null;
      return response.json();
    }

    applyCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      var count = 0;
      cart.items.forEach((item) => {
        var variantId = String(item.variant_id || item.id || '');
        if (variantId && this.eligibleVariantIds.has(variantId)) {
          count += toQuantity(item.quantity);
        }
      });
      this.cartCount = count;
      if (this.progressEl) this.progressEl.dataset.cartCount = String(count);
      this.updateState();
    }

    cartAddUrl() {
      return withJsonSuffix(
        this.dataset.cartAddUrl ||
        (window.theme && theme.routes && theme.routes.cartAdd) ||
        '/cart/add'
      );
    }

    selectedItems() {
      return this.cards.map((card) => {
        var quantity = this.selected.get(String(card.dataset.variantId));
        if (!quantity) return null;
        return {
          id: Number(card.dataset.variantId),
          quantity: quantity
        };
      }).filter(Boolean);
    }

    sectionsToRender() {
      var sections = ['cart-icon-bubble'];
      var cartDrawer = document.querySelector('cart-drawer');
      var drawerSection = cartDrawer && cartDrawer.closest('.shopify-section');
      if (drawerSection && drawerSection.id) {
        sections.push(drawerSection.id.replace('shopify-section-', ''));
      }
      return unique(sections);
    }

    renderReturnedSections(sections) {
      if (!sections || typeof sections !== 'object') return;
      var cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = sections['cart-icon-bubble'];
      }
    }

    refreshAndOpenCartDrawer() {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      if (this._cartDrawerTimer) clearTimeout(this._cartDrawerTimer);
      this._cartDrawerTimer = setTimeout(() => {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          bubbles: true,
          detail: { opener: this.addButtonEl }
        }));
        this._cartDrawerTimer = null;
      }, 700);
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    cartErrorMessage(data, status) {
      if (data) {
        if (typeof data.description === 'string') return data.description;
        if (typeof data.message === 'string') return data.message;
        if (data.errors && typeof data.errors === 'object') {
          return Object.keys(data.errors).map((key) => {
            var value = data.errors[key];
            return Array.isArray(value) ? value.join(', ') : value;
          }).join(', ');
        }
      }
      return (window.theme && theme.strings && theme.strings.cartError) || '';
    }

    clearSelection() {
      var ids = Array.from(this.selected.keys());
      this.selected.clear();
      ids.forEach((id) => this.syncVariantInput(id, 0));
      this.updateState();
    }

    async addToCart() {
      var items = this.selectedItems();
      if (this.loading || !items.length) return;

      this.loading = true;
      this.classList.add('is-loading');
      this.setError('');
      this.updateState();

      try {
        var response = await fetch(this.cartAddUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: items,
            sections: this.sectionsToRender(),
            sections_url: window.location.pathname
          })
        });
        var data = await response.json().catch(() => ({}));

        if (!response.ok || data.status) {
          throw new Error(this.cartErrorMessage(data, response.status));
        }

        this.renderReturnedSections(data.sections);
        var freshCart = await this.fetchCart();
        if (freshCart) this.applyCart(freshCart);

        this.clearSelection();
        document.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: {
            cart: freshCart || null,
            variantId: items[0] ? items[0].id : null
          }
        }));
        this.refreshAndOpenCartDrawer();
      } catch (error) {
        this.setError(error.message || (window.theme && theme.strings && theme.strings.cartError) || '');
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
        this.updateState();
      }
    }

    syncRegiosPrices() {
      var api = window.RegiosDOPP?.api?.v0;
      if (!api) return;

      this.cards.forEach((card) => {
        var args = parseCardArgs(card);
        if (!args) return;

        var interop = api.themeInterop;
        if (interop?.updateOnPagePrices) {
          interop.updateOnPagePrices(card, card.dataset.regiosDoppExtensionType || 'COLLECTION_PAGE', args).catch(() => {});
        }

        if (api.calculateDiscountedPrices) {
          api.calculateDiscountedPrices(args)
            .then((result) => renderPrice(card, result))
            .catch(() => {});
        }
      });
    }
  }

  if (!customElements.get('labor-day-variant-deals')) {
    customElements.define('labor-day-variant-deals', LaborDayVariantDeals);
  }
})();
